import { Injectable, signal, computed, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { toObservable } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { Language, RTL_LANGUAGES } from '../enums/language.enum';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private readonly STORAGE_KEY = 'LOCALIZE_DEFAULT_LANGUAGE';
  private readonly DEFAULT_LANGUAGE = Language.ENGLISH;
  private readonly SUPPORTED_LANGUAGES = [Language.ENGLISH, Language.ARABIC];

  private currentLanguageSignal = signal<Language>(this.DEFAULT_LANGUAGE);

  readonly currentLanguage = this.currentLanguageSignal.asReadonly();
  readonly isRtl = computed(() => RTL_LANGUAGES.includes(this.currentLanguageSignal()));
  readonly direction = computed(() => (this.isRtl() ? 'rtl' : 'ltr'));

  // Observable for components that need to react to language changes
  readonly languageChanged$ = toObservable(this.currentLanguageSignal);

  constructor(
    private translate: TranslateService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    void this.initializeLanguage();
  }

  private async initializeLanguage(): Promise<void> {
    this.translate.addLangs(this.SUPPORTED_LANGUAGES);

    const storedLang = this.getStoredLanguage();
    const initialLang = storedLang || this.DEFAULT_LANGUAGE;

    await this.setLanguage(initialLang);
  }

  async setLanguage(lang: Language): Promise<void> {
    if (!this.SUPPORTED_LANGUAGES.includes(lang)) {
      lang = this.DEFAULT_LANGUAGE;
    }

    this.currentLanguageSignal.set(lang);
    this.translate.setDefaultLang(lang);
    await firstValueFrom(this.translate.use(lang));
    this.storeLanguage(lang);
    this.updateDocumentDirection();
  }

  async toggleLanguage(): Promise<void> {
    const newLang =
      this.currentLanguageSignal() === Language.ENGLISH
        ? Language.ARABIC
        : Language.ENGLISH;
    await this.setLanguage(newLang);
  }

  getAvailableLanguages(): Language[] {
    return [...this.SUPPORTED_LANGUAGES];
  }

  private getStoredLanguage(): Language | null {
    if (isPlatformBrowser(this.platformId)) {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored && this.SUPPORTED_LANGUAGES.includes(stored as Language)) {
        return stored as Language;
      }
    }
    return null;
  }

  private storeLanguage(lang: Language): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(this.STORAGE_KEY, lang);
    }
  }

  private updateDocumentDirection(): void {
    if (isPlatformBrowser(this.platformId)) {
      const html = document.documentElement;
      html.setAttribute('dir', this.direction());
      html.setAttribute('lang', this.currentLanguageSignal());
    }
  }
}
